import { useEffect, useState } from 'react'
import { RouterProvider } from 'react-router-dom'
import { RefreshCw, X } from 'lucide-react'
import { router } from '@/routes'
import { useAuthListener } from '@/features/auth/use-auth-listener'
import { ToastHost } from '@/shared/ui/toast'
import { trackPageView } from '@/core/analytics'
import { applyUpdate, useUpdateAvailable, useSwUpdateWaiting, APP_VERSION } from '@/core/firebase/version'
import { updateAlreadyAttempted, clearUpdateAttemptIfMoved } from '@/core/firebase/update-attempt'
import { refreshPushToken } from '@/core/firebase/push'
import { useAuthStore } from '@/core/auth/auth-store'

// The QueryClientProvider lives in main.tsx (single client, app-wide defaults).
export function App() {
  // Mirror Firebase auth state into the store for the whole app.
  useAuthListener()
  const authed = useAuthStore((s) => s.status === 'authenticated')
  // DEV: `?update=1` force-shows the banner for design walks/reviews.
  const forceBanner =
    import.meta.env.DEV && new URLSearchParams(window.location.search).has('update')
  // Two independent signals, either one enough: the Firestore version doc
  // (works across tabs/devices, but blind to a redeploy under the same version)
  // and the service worker's own waiting-worker event (blind to nothing, but
  // only fires in this tab). Before the second one was wired, a returning
  // visitor on a same-version redeploy was pinned to their cached shell with no
  // way to know — including on the share door, where strangers arrive.
  // Both hooks called unconditionally — `a() || b()` short-circuits, which
  // makes b() a conditional hook and breaks hook order the moment a() is true.
  const versionBehind = useUpdateAvailable(authed)
  const swWaiting = useSwUpdateWaiting()
  // We already reloaded from this exact version and came back on it, so the
  // server has nothing newer and offering the button again would only loop.
  // Same contract as clubmanager — see core/firebase/update-attempt.ts.
  const alreadyTried = updateAlreadyAttempted(APP_VERSION)
  clearUpdateAttemptIfMoved(APP_VERSION)
  const updateAvailable = (versionBehind || swWaiting || forceBanner) && !alreadyTried
  // FCM tokens rotate — re-register on every authenticated boot (silent:
  // only runs when permission is ALREADY granted; never prompts).
  const uid = useAuthStore((s) => s.user?.uid ?? null)
  const anonymous = useAuthStore((s) => s.user?.isAnonymous ?? false)
  useEffect(() => {
    if (authed && uid && !anonymous) void refreshPushToken(uid)
  }, [authed, uid, anonymous])
  // GA4 page_view on every route commit (initial load included).
  useEffect(() => {
    trackPageView(router.state.location.pathname)
    return router.subscribe((state) => trackPageView(state.location.pathname))
  }, [])
  return (
    <>
      <RouterProvider router={router} />
      {updateAvailable && <UpdateBanner />}
      <ToastHost />
    </>
  )
}

// Update toast (styleguide: quiet surface, ONE small accent action — the old
// full-width accent slab out-shouted every CTA on screen). Dismiss lasts the
// session; the banner returns next open until the update applies. DEV:
// `?update=1` force-shows it for design walks.
function UpdateBanner() {
  const [dismissed, setDismissed] = useState(false)
  const [applying, setApplying] = useState(false)
  if (dismissed) return null
  return (
    <div className="fixed inset-x-0 bottom-[var(--floating-bottom)] z-50 px-4 pointer-events-none">
      <div className="um-enter pointer-events-auto max-w-screen-sm mx-auto flex items-center gap-3 pl-4 pr-2 py-2 rounded-[var(--radius-card)] bg-[var(--surface-card)] border border-[var(--line)] shadow-[var(--shadow-popover)]">
        <RefreshCw
          size={16}
          className={`shrink-0 text-[color:var(--accent)] ${applying ? 'motion-safe:animate-spin' : ''}`}
        />
        <span className="flex-1 text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
          Nueva versión lista
        </span>
        <button
          type="button"
          disabled={applying}
          onClick={() => {
            setApplying(true)
            applyUpdate()
          }}
          className="shrink-0 min-h-[var(--tap)] px-4 rounded-full bg-[var(--accent-wash)] text-[color:var(--accent)] text-[length:var(--fs-sm)] font-semibold shadow-[inset_0_0_0_1px_var(--accent)] active:scale-[0.97] transition-transform"
        >
          {applying ? 'Actualizando…' : 'Actualizar'}
        </button>
        <button
          type="button"
          aria-label="Recordar más tarde"
          onClick={() => setDismissed(true)}
          className="shrink-0 h-[var(--tap)] w-[var(--tap)] inline-flex items-center justify-center rounded-full text-[color:var(--fg-faint)] hover:bg-[var(--line-soft)]"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  )
}
